import { useEffect, useState } from 'react';
import { GitCompare, Download, AlertCircle, Loader2, Info, Plus, X, Search } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useTerritoryStore } from '@/stores/territoryStore';
import {
  useComparisonTerritories,
  useComparaisonIndicators,
  usePeersMetrics,
  type CompareMetrics,
  type TerritoryEntry,
} from '@/hooks/useComparaison';

// ── Design tokens (inlined) ───────────────────────────────────────────────────

const NAVY = '#1b2559';
const NAVY_LIGHT = '#c7cfe8';
const NAVY_600 = '#2c3a78';
const MARIANNE = '#e1000f';
const SUCCESS = '#1f8a5b';
const DANGER_BG = '#fdecec';
const LINE = '#e4e8f0';

// ── Indicator definitions ─────────────────────────────────────────────────────

interface IndicatorDef {
  key: keyof CompareMetrics;
  label: string;
  unit: string;
  color: string;           // pilot bar colour
  higherIsBetter: boolean; // for context notes
}

const INDICATORS: IndicatorDef[] = [
  { key: 'densite', label: 'Densité de population', unit: 'hab/km²', color: NAVY, higherIsBetter: false },
  { key: 'sols_artificialises', label: 'Sols artificialisés', unit: '%', color: MARIANNE, higherIsBetter: false },
  { key: 'canopee', label: 'Canopée', unit: '%', color: SUCCESS, higherIsBetter: true },
  { key: 'passoires', label: 'Passoires énergétiques (F+G)', unit: '%', color: '#ef7d18', higherIsBetter: false },
  { key: 'solaire', label: 'Potentiel solaire (est.)', unit: 'GWh/an', color: '#ffd24a', higherIsBetter: true },
  { key: 'croissance', label: 'Croissance démographique', unit: '%', color: NAVY_600, higherIsBetter: true },
];

// ── Indice de pression territoriale (composite Terravision — BETA) ─────────────

/**
 * Indice de pression : composite 0-100 (plus haut = plus de pression), calculé
 * UNIQUEMENT depuis les indicateurs RÉELS du pilote. Normalisations absolues
 * documentées ci-dessous ; une sous-score dont l'indicateur source est absent
 * (null) n'est pas comptée — aucune valeur n'est inventée. L'indice global est
 * la moyenne des sous-scores disponibles (null si aucun).
 */
const clampPct = (n: number): number => Math.max(0, Math.min(100, n));

interface PressureSubscore {
  label: string;
  score: number;
  color: string;
}

interface PressureIndex {
  global: number | null;
  subscores: PressureSubscore[];
}

function computePressureIndex(m: CompareMetrics): PressureIndex {
  const subscores: PressureSubscore[] = [];
  // Foncier : % de sols artificialisés, directement (déjà sur une échelle 0-100).
  if (m.sols_artificialises != null)
    subscores.push({ label: 'Foncier', score: Math.round(clampPct(m.sols_artificialises)), color: MARIANNE });
  // Énergie : % de passoires F+G ; 40 % → pression maximale.
  if (m.passoires != null)
    subscores.push({ label: 'Énergie', score: Math.round(clampPct(m.passoires * 2.5)), color: '#ffd24a' });
  // Biodiversité : canopée ; 25 % de couvert → pression nulle, 0 % → maximale.
  if (m.canopee != null)
    subscores.push({ label: 'Biodiversité', score: Math.round(clampPct(100 - m.canopee * 4)), color: SUCCESS });
  // Démographie : densité ; ~12 000 hab/km² → pression maximale.
  if (m.densite != null)
    subscores.push({ label: 'Démographie', score: Math.round(clampPct(m.densite / 120)), color: '#7fa6ff' });

  const global = subscores.length
    ? Math.round(subscores.reduce((s, x) => s + x.score, 0) / subscores.length)
    : null;
  return { global, subscores };
}

/** Qualifie l'indice de pression global en libellé lisible. */
function pressureLabel(score: number): string {
  if (score >= 75) return 'élevée';
  if (score >= 50) return 'modérée–élevée';
  if (score >= 25) return 'modérée';
  return 'faible';
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface BarEntry {
  name: string;
  value: number;
  isPilot: boolean;
  isDemo: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: BarEntry; value: number }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  if (!entry) return null;
  const d = entry.payload;
  return (
    <div className="rounded-[10px] border border-line bg-white px-3 py-2 shadow-card text-[12px]">
      <p className="font-semibold text-ink">{d.name}</p>
      <p className="text-muted">
        {entry.value.toLocaleString('fr-FR')}
        {d.isDemo && (
          <span className="ml-1 text-[10px] text-faint">(démo)</span>
        )}
      </p>
    </div>
  );
}

interface IndicatorBarCardProps {
  def: IndicatorDef;
  entries: BarEntry[];
}

function IndicatorBarCard({ def, entries }: IndicatorBarCardProps) {
  const validEntries = entries.filter((e) => e.value != null && !Number.isNaN(e.value));

  if (!validEntries.length) {
    return (
      <div className="rounded-card border border-line bg-white p-4 shadow-card">
        <p className="text-[13px] font-bold text-ink mb-1">{def.label}</p>
        <p className="text-[12px] text-faint">Données non disponibles.</p>
      </div>
    );
  }

  const maxVal = Math.max(...validEntries.map((e) => e.value));

  return (
    <div className="rounded-card border border-line bg-white p-4 shadow-card">
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-[13px] font-bold text-ink">{def.label}</p>
        <span className="font-mono text-[11px] text-faint">{def.unit}</span>
      </div>

      {/* Manual bars — one per territory */}
      <div className="flex flex-col gap-2.5">
        {validEntries.map((entry) => {
          const pct = maxVal > 0 ? (entry.value / maxVal) * 100 : 0;
          const barColor = entry.isPilot ? def.color : NAVY_LIGHT;
          return (
            <div key={entry.name} className="flex items-center gap-2.5">
              <span
                className="w-28 shrink-0 truncate text-right text-[11.5px]"
                style={{ color: entry.isPilot ? NAVY : '#6b7392', fontWeight: entry.isPilot ? 700 : 400 }}
                title={entry.name}
              >
                {entry.name}
              </span>
              <div className="relative flex-1 h-5 overflow-hidden rounded-full bg-surface-subtle">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: barColor }}
                />
              </div>
              <span
                className="w-16 shrink-0 text-right font-mono text-[11.5px]"
                style={{ color: entry.isPilot ? NAVY : '#6b7392', fontWeight: entry.isPilot ? 700 : 400 }}
              >
                {entry.value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                {entry.isDemo && (
                  <span className="text-[9px] text-faint ml-0.5">✦</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Recharts hidden bar chart for semantic structure (screen readers) */}
      <div className="sr-only">
        <ResponsiveContainer width="100%" height={validEntries.length * 32}>
          <BarChart data={validEntries} layout="vertical">
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" aria-label={def.label}>
              {validEntries.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={entry.isPilot ? def.color : NAVY_LIGHT}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────────

interface ChipProps {
  label: string;
  selected: boolean;
  always?: boolean;
  isDemo?: boolean;
  onToggle: () => void;
}

function TerritoryChip({ label, selected, always = false, isDemo = false, onToggle }: ChipProps) {
  return (
    <button
      type="button"
      onClick={always ? undefined : onToggle}
      disabled={always}
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-all',
        selected
          ? 'bg-navy text-white shadow-sm'
          : 'border border-line bg-white text-muted hover:border-navy hover:text-navy',
        always ? 'cursor-default' : 'cursor-pointer',
      ].join(' ')}
      aria-pressed={selected}
      aria-label={`${always ? 'Territoire pilote' : 'Comparer'} ${label}${isDemo ? ' (données de démonstration)' : ''}`}
    >
      {always && (
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: selected ? '#7fa6ff' : '#6b7392' }}
          aria-hidden="true"
        />
      )}
      {label}
      {isDemo && (
        <span className="text-[10px] opacity-60" aria-hidden="true">✦</span>
      )}
    </button>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

/**
 * Comparaison — 7ème écran Terravision.
 *
 * Compare le territoire pilote avec ses voisins sur 6 indicateurs clés.
 * Si un seul territoire réel est disponible en base, les territoires pairs
 * sont affichés comme données de DÉMONSTRATION (clairement indiqué).
 */
export function Comparaison() {
  const { activeTerritory } = useTerritoryStore();
  const pilotInsee = activeTerritory?.code_insee ?? null;

  // Fetch territory list from the API
  const { data: territories, isLoading: teLoading, error: teError } = useComparisonTerritories();

  // Fetch indicators for the pilot territory
  const { metrics: pilotMetrics, isLoading: indLoading } = useComparaisonIndicators(pilotInsee);

  // Candidats à la comparaison = communes aux données complètes (has_full_data),
  // hors pilote. Pool réel et restreint : les communes « légères » (sans données)
  // ne sont pas comparables (barres vides). Les métriques sont chargées pour tout
  // le pool (petit ensemble) ; la sélection ci-dessous décide de l'affichage.
  const peerPool: TerritoryEntry[] = (territories ?? []).filter(
    (t) => t.code_insee !== pilotInsee && t.has_full_data === true,
  );
  const poolCodes = peerPool.map((t) => t.code_insee);
  const { byInsee: peerMetrics, isLoading: peersLoading } = usePeersMetrics(poolCodes);

  // Sélecteur OPT-IN plafonné. `selected === null` = pas encore initialisé : on
  // pré-remplit avec les 2 communes les plus proches en population (écran utile
  // au chargement) sans imposer toute la liste.
  const MAX_SELECTED = 3;
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  useEffect(() => {
    if (selected !== null || peerPool.length === 0 || !activeTerritory) return;
    const pop = activeTerritory.population ?? 0;
    const nearest = [...peerPool]
      .sort((a, b) => Math.abs((a.population ?? 0) - pop) - Math.abs((b.population ?? 0) - pop))
      .slice(0, 2)
      .map((t) => t.code_insee);
    setSelected(new Set(nearest));
  }, [peerPool, selected, activeTerritory]);

  const selectedSet = selected ?? new Set<string>();
  const addPeer = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.size < MAX_SELECTED) next.add(code);
      return next;
    });
    setPickerOpen(false);
    setPickerQuery('');
  };
  const removePeer = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      next.delete(code);
      return next;
    });
  };

  const selectedPeers = peerPool
    .filter((t) => selectedSet.has(t.code_insee))
    .map((t) => ({ code: t.code_insee, name: t.name, metrics: peerMetrics[t.code_insee] }));

  // Candidats ajoutables (pool moins sélectionnés), filtrés par la recherche.
  const q = pickerQuery.trim().toLowerCase();
  const pickerCandidates = peerPool.filter(
    (t) =>
      !selectedSet.has(t.code_insee) &&
      (q === '' || t.name.toLowerCase().includes(q) || t.code_insee.includes(q)),
  );
  const canAddMore = selectedSet.size < MAX_SELECTED && pickerCandidates.length > 0;

  // Build bar entries per indicator (valeurs RÉELLES uniquement ; un indicateur
  // absent est simplement omis — jamais remplacé par une valeur estimée).
  const buildEntries = (key: keyof CompareMetrics): BarEntry[] => {
    const pilotVal = pilotMetrics[key];
    const entries: BarEntry[] = [];

    // Pilot always first
    if (pilotVal != null) {
      entries.push({
        name: activeTerritory?.name ?? '—',
        value: pilotVal,
        isPilot: true,
        isDemo: false,
      });
    }

    // Active peers
    for (const peer of selectedPeers) {
      const val = peer.metrics[key];
      if (val != null) {
        entries.push({
          name: peer.name,
          value: val,
          isPilot: false,
          isDemo: false,
        });
      }
    }

    return entries;
  };

  // ── No territory selected ──────────────────────────────────────────────────

  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour accéder à la Comparaison.
        </p>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (teError) {
    return (
      <div
        className="m-6 flex items-start gap-3 rounded-card border p-4"
        style={{ background: DANGER_BG, borderColor: '#f5c6c6' }}
        role="alert"
      >
        <AlertCircle size={18} style={{ color: MARIANNE, marginTop: 1, flexShrink: 0 }} />
        <div>
          <p className="font-semibold text-[13px] text-ink">
            Impossible de charger la liste des territoires
          </p>
          <p className="text-[12px] text-muted mt-0.5">
            {(teError as Error).message ?? 'Erreur réseau. Réessayez.'}
          </p>
        </div>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  if (teLoading || indLoading || peersLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-3 p-8">
        <Loader2 size={20} className="animate-spin text-navy" />
        <p className="text-sm text-muted">Chargement de la comparaison…</p>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  const pressure = computePressureIndex(pilotMetrics);
  const noPeers = peerPool.length === 0;
  const missingPilotMetrics = (Object.keys(pilotMetrics) as Array<keyof CompareMetrics>).some(
    (key) => pilotMetrics[key] === null,
  );

  // Lecture comparative : générée UNIQUEMENT pour les indicateurs réels présents.
  const fmt = (n: number, d = 1): string =>
    n.toLocaleString('fr-FR', { maximumFractionDigits: d });
  const comparativeNotes: { color: string; bg: string; text: string }[] = [];
  if (pilotMetrics.sols_artificialises != null)
    comparativeNotes.push({
      color: MARIANNE,
      bg: DANGER_BG,
      text: `Sols artificialisés : ${fmt(pilotMetrics.sols_artificialises)} % du territoire. Le suivi ZAN conditionne la trajectoire 2031.`,
    });
  if (pilotMetrics.solaire != null)
    comparativeNotes.push({
      color: SUCCESS,
      bg: '#eaf6ef',
      text: `Potentiel solaire estimé à ${fmt(pilotMetrics.solaire)} GWh/an sur l'emprise bâtie — un gisement mobilisable pour la transition.`,
    });
  if (pilotMetrics.passoires != null)
    comparativeNotes.push({
      color: '#ef7d18',
      bg: '#fff4e5',
      text: `${fmt(pilotMetrics.passoires)} % du parc diagnostiqué est classé F ou G : cible prioritaire d'un programme de rénovation.`,
    });
  if (pilotMetrics.densite != null)
    comparativeNotes.push({
      color: NAVY,
      bg: '#eef2fb',
      text: `Densité de ${fmt(pilotMetrics.densite, 0)} hab/km².`,
    });

  return (
    <div className="flex flex-col gap-5 p-[22px_26px_40px]">

      {/* Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <GitCompare size={20} style={{ color: NAVY }} aria-hidden="true" />
          <h1 className="font-display text-[22px] font-bold text-ink">
            Comparaison territoriale
          </h1>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:opacity-90"
          style={{ background: NAVY }}
        >
          <Download size={14} aria-hidden="true" />
          Exporter
        </button>
      </div>

      {/* Note honnête sur la disponibilité des données (jamais de valeurs estimées) */}
      {(noPeers || missingPilotMetrics) && (
        <div
          className="flex items-start gap-2.5 rounded-card border px-4 py-3 text-[12px]"
          style={{ background: '#eef2fb', borderColor: '#cdd8f0' }}
          role="note"
          aria-label="Note sur la disponibilité des données"
        >
          <Info size={15} style={{ color: NAVY_600, marginTop: 1, flexShrink: 0 }} aria-hidden="true" />
          <p className="text-muted">
            {noPeers ? (
              <>
                <span className="font-semibold text-ink">Aucune commune comparable.</span>{' '}
                {activeTerritory.name} ({activeTerritory.code_insee}) est pour l&apos;instant le
                seul territoire aux données complètes. Chargez les données d&apos;une commune
                voisine pour activer la comparaison.
              </>
            ) : (
              <>
                <span className="font-semibold text-ink">Indicateurs partiels.</span>{' '}
                Certains indicateurs de {activeTerritory.name} ne sont pas encore disponibles ;
                ils sont affichés « non disponibles ». Aucune valeur n&apos;est estimée.
              </>
            )}
          </p>
        </div>
      )}

      {/* Sélecteur de territoires à comparer ──────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Sélection des territoires à comparer">
        {/* Pilote — toujours actif */}
        <TerritoryChip label={activeTerritory.name} selected always onToggle={() => undefined} />

        {/* Pairs sélectionnés (retirables) */}
        {selectedPeers.map((peer) => (
          <span
            key={peer.code}
            className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-1.5 text-[12.5px] font-semibold text-white shadow-sm"
          >
            {peer.name}
            <button
              type="button"
              onClick={() => removePeer(peer.code)}
              aria-label={`Retirer ${peer.name} de la comparaison`}
              className="-mr-1 rounded-full p-0.5 transition-colors hover:bg-white/20"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}

        {/* Ajouter une commune comparable (sélecteur avec recherche) */}
        {canAddMore && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-line bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-muted transition-colors hover:border-navy hover:text-navy"
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              <Plus size={13} aria-hidden="true" />
              Ajouter une commune
            </button>
            {pickerOpen && (
              <div className="absolute left-0 top-full z-30 mt-1.5 w-64 overflow-hidden rounded-[11px] border border-line bg-white shadow-card">
                <div className="border-b border-line p-2">
                  <div className="relative flex items-center">
                    <Search size={13} className="pointer-events-none absolute left-2.5 text-muted" />
                    <input
                      type="search"
                      autoFocus
                      value={pickerQuery}
                      onChange={(e) => setPickerQuery(e.target.value)}
                      placeholder="Rechercher une commune…"
                      className="h-8 w-full rounded-[9px] border border-line bg-surface-subtle pl-7 pr-2.5 font-sans text-[12px] text-body placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-navy"
                      aria-label="Rechercher une commune à comparer"
                    />
                  </div>
                </div>
                <ul role="listbox" className="max-h-56 overflow-auto py-1">
                  {pickerCandidates.length === 0 ? (
                    <li className="px-3 py-3 text-center font-sans text-[12px] text-muted">
                      Aucune commune disponible.
                    </li>
                  ) : (
                    pickerCandidates.map((t) => (
                      <li key={t.code_insee} role="option" aria-selected={false}>
                        <button
                          type="button"
                          onClick={() => addPeer(t.code_insee)}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-surface-subtle"
                        >
                          <span className="truncate font-sans text-[12.5px] font-medium text-body">
                            {t.name}
                          </span>
                          <span className="flex-shrink-0 font-mono text-[10px] text-muted">
                            {t.code_insee}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {selectedSet.size >= MAX_SELECTED && (
          <span className="text-[11px] text-faint">Maximum {MAX_SELECTED} communes comparées.</span>
        )}
      </div>

      {/* Main grid ───────────────────────────────────────────────────────── */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '1fr 320px' }}>

        {/* LEFT: Indicator bar charts ────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4 content-start">
          {INDICATORS.map((def) => (
            <IndicatorBarCard
              key={def.key}
              def={def}
              entries={buildEntries(def.key)}
            />
          ))}
        </div>

        {/* RIGHT: Lecture comparative + Indice de pression ──────────────── */}
        <div className="flex flex-col gap-4">

          {/* Lecture comparative */}
          <div className="rounded-panel border border-line bg-white p-5 shadow-card">
            <h3 className="font-display text-[14px] font-bold text-ink mb-4">
              Lecture comparative
            </h3>
            <div className="flex flex-col gap-3.5">
              {comparativeNotes.length === 0 ? (
                <p className="text-[12px] text-faint">
                  Indicateurs non encore disponibles pour {activeTerritory.name}.
                </p>
              ) : (
                comparativeNotes.map((note, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 rounded-[10px] p-3 text-[12px] text-body"
                    style={{ background: note.bg }}
                  >
                    <span
                      className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                      style={{ background: note.color }}
                      aria-hidden="true"
                    />
                    <p className="leading-relaxed">{note.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Indice de pression territoriale */}
          <div
            className="rounded-panel border p-5 shadow-card"
            style={{ background: 'linear-gradient(145deg,#1b2559,#2c3a78)', borderColor: '#2c3a78' }}
            aria-label="Indice de pression territoriale"
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-300">
                Indice de pression territoriale
              </p>
              <span
                className="rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide"
                style={{ background: 'rgba(255,210,74,0.18)', color: '#ffd24a' }}
                title="Composite Terravision calculé depuis les indicateurs réels du territoire"
              >
                BETA
              </span>
            </div>

            {pressure.global == null ? (
              <p className="py-6 text-[12px] leading-relaxed text-blue-200">
                Données insuffisantes pour calculer l&apos;indice : aucun indicateur source
                n&apos;est encore disponible pour ce territoire.
              </p>
            ) : (
              <>
                <div className="flex items-end gap-2 mb-3">
                  <span className="font-display text-[48px] font-extrabold leading-none text-white">
                    {pressure.global}
                  </span>
                  <span className="mb-2 font-mono text-[16px] text-blue-200">/ 100</span>
                </div>

                {/* Score bar */}
                <div className="mb-3 h-2 overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pressure.global}%`,
                      background: 'linear-gradient(90deg,#ffd24a,#ef7d18)',
                    }}
                    aria-label={`${pressure.global}% de pression`}
                  />
                </div>

                <p className="text-[11.5px] leading-relaxed text-blue-200">
                  Pression{' '}
                  <span className="font-semibold text-white">
                    {pressureLabel(pressure.global)}
                  </span>
                  , calculée sur {pressure.subscores.length} indicateur
                  {pressure.subscores.length > 1 ? 's' : ''} disponible
                  {pressure.subscores.length > 1 ? 's' : ''}.
                </p>

                {/* Sub-scores */}
                <div className="mt-4 flex flex-col gap-1.5">
                  {pressure.subscores.map(({ label, score, color }) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="w-20 text-[10.5px] text-blue-300">{label}</span>
                      <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-white/20">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${score}%`, background: color }}
                        />
                      </div>
                      <span className="w-6 text-right font-mono text-[10.5px] text-blue-200">
                        {score}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <p
              className="mt-4 text-[10.5px] leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              Composite Terravision (BETA), normalisations absolues documentées, calculé
              depuis les indicateurs réels (artificialisation, passoires, canopée, densité).
            </p>
          </div>

        </div>
      </div>

      {/* Footer ─────────────────────────────────────────────────────────── */}
      <p className="pb-1 text-[10.5px] text-faint" style={{ borderTop: `1px solid ${LINE}`, paddingTop: '12px' }}>
        Sources : BDNB (bâti, DPE), BD TOPO IGN, INSEE, estimation solaire interne. Pairs :
        communes aux données complètes uniquement (aucune donnée estimée). Indice de pression
        territoriale : indicateur composite Terravision (BETA).
      </p>
    </div>
  );
}
